import React, { useState, useEffect } from 'react';
import BRANDING from './config/branding';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './index.css'; // Tailwind CSS

// Landing Page Components - Now importing from compatibility wrappers
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import Features from './components/Features';
import Pricing from './components/Pricing';
import API from './components/API';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Footer from './components/Footer';

// Dashboard Pages
import Login from './pages/Login';
import DashboardHome from './pages/DashboardHome';
import APIKeys from './pages/APIKeys';
import Logs from './pages/Logs';
import Profile from './pages/Profile';
import Billing from './pages/Billing';
import ProtectedRoute from './components/ProtectedRoute';
import SendWhatsAppMessage from './pages/SendWhatsAppMessage';
import LinkPhoneNumbers from './pages/LinkPhoneNumbers';
import ApiDocs from './pages/ApiDocs';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';

// Admin Pages
import AdminHome from './admin/pages/AdminHome';
import AdminUsers from './admin/pages/AdminUsers';
import AdminAddBalance from './admin/pages/AdminAddBalance';
import AdminPlans from './admin/pages/AdminPlans';
import AdminStats from './admin/pages/AdminStats';
import AdminAccounts from './admin/pages/AdminAccounts';

// Landing Page Component
function LandingPage({ language, toggleLanguage }) {
  return (
    <div className="App" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Navbar language={language} toggleLanguage={toggleLanguage} />
      <Hero language={language} />
      <HowItWorks language={language} />
      <Features language={language} />
      <Pricing language={language} />
      <API language={language} />
      <Testimonials language={language} />
      <FAQ language={language} />
      <Contact language={language} />
      <Footer language={language} />
    </div>
  );
}

function App() {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    document.title = BRANDING[language];
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
    document.documentElement.lang = language === 'en' ? 'ar' : 'en';
    document.documentElement.dir = language === 'en' ? 'rtl' : 'ltr';
  };

  return (
    <Router>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        toastClassName="custom-toast"
        bodyClassName="custom-toast-body"
      />
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<LandingPage language={language} toggleLanguage={toggleLanguage} />} />

        {/* Legal Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicy language={language} />} />

        {/* API Documentation */}
        <Route path="/otp/api-docs" element={<ApiDocs language={language} />} />

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />

        {/* Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/api-keys"
          element={
            <ProtectedRoute>
              <APIKeys />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/logs"
          element={
            <ProtectedRoute>
              <Logs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/send-message"
          element={
            <ProtectedRoute>
              <SendWhatsAppMessage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/link-phones"
          element={
            <ProtectedRoute>
              <LinkPhoneNumbers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/billing"
          element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/add-balance"
          element={
            <ProtectedRoute>
              <AdminAddBalance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/plans"
          element={
            <ProtectedRoute>
              <AdminPlans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/accounts"
          element={
            <ProtectedRoute>
              <AdminAccounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/stats"
          element={
            <ProtectedRoute>
              <AdminStats />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
